import React, { useEffect, useState } from "react";
import { baseUrl } from "../../config.js";
import useToken from "../../useToken.js";
import Cargando from '../../img/Cargando.gif';
import './Stock.css';

const labelPosicion = (warehouse) => {
    const partes = (warehouse || '').split('/');
    if (partes.length <= 2) return null;
    const raw = partes[partes.length - 1];
    return raw.replace(/^POS-R(\d+)-C(\d+)$/i, 'F$1-C$2').replace(/^POS-/, '');
};

const labelCelda = (name, row, col) => {
    if (row && col) return `F${row}-C${col}`;
    return (name || '').replace(/^POS-R(\d+)-C(\d+)$/i, 'F$1-C$2');
};

const IconWarehouse = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5Z" />
        <path d="M9 21v-7h6v7" />
    </svg>
);

const IconRefresh = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.6-6.3" />
        <path d="M21 3v6h-6" />
    </svg>
);

const IconMap = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 3 3 5.5V19l6-2 6 2 6-2.5V5l-6 2.5L9 3Z" />
        <path d="M9 3v14M15 5.5V19" />
    </svg>
);

const IconBox = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 8H3l2-4h14l2 4Z" />
        <path d="M3 8v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8" />
        <path d="M12 8v12" />
    </svg>
);

const Stock = () => {
    const { token } = useToken();

    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    // layout de posiciones por almacén (GET /my/store/:idStore/layout): se
    // pide solo bajo demanda, al hacer clic en el almacén (no por default)
    const [productos, setProductos] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [nuevo, setNuevo] = useState({ name: '', uomId: '' });
    const [creando, setCreando] = useState(false);
    const [msgProducto, setMsgProducto] = useState('');
    const [layouts, setLayouts] = useState({});
    const [mapaAbierto, setMapaAbierto] = useState(null);
    const [cargandoMapa, setCargandoMapa] = useState(null);
    const [celdaSel, setCeldaSel] = useState(null);

    useEffect(() => {
        if (!token) return;
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const toggleMapa = (idStore) => {
        if (mapaAbierto === idStore) {
            setMapaAbierto(null);
            return;
        }
        setMapaAbierto(idStore);
        const cached = layouts[idStore];
        if (cached && (cached.grid || (cached.levels && cached.levels.length))) return;
        setCargandoMapa(idStore);
        fetch(baseUrl + "my/store/" + idStore + "/layout", {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json()).then(json => {
            if (json?.success) {
                setLayouts(prev => ({ ...prev, [idStore]: json.data }));
            }
        }).catch(e => console.error(e))
          .finally(() => setCargandoMapa(null));
    };

    const grupos = data.reduce((acc, q) => {
        const key = q.idStore || 0;
        if (!acc[key]) acc[key] = { idStore: q.idStore, storeName: q.storeName || `Almacén ${q.idStore}`, rows: [] };
        acc[key].rows.push(q);
        return acc;
    }, {});

    const listaGrupos = Object.values(grupos);
    const disponibles = data.reduce((s, q) => s + (q.available || 0), 0);
    // Coincide con "Mis productos" (catálogo propio), no con las líneas de stock
    // (que también incluyen el Pallet genérico de Odoo).
    const productosCatalogo = productos.length;

    const cargarProductos = async () => {
        try {
            const [prods, unidades] = await Promise.all([
                fetch(baseUrl + "my/products", { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
                fetch(baseUrl + "my/uoms", { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
            ]);
            setProductos(prods?.data || []);
            setUoms(unidades?.data || []);
        } catch (e) {
            console.error(e);
        }
    };

    const crearProducto = async () => {
        if (!nuevo.name.trim()) {
            setMsgProducto('Ingresa el nombre del producto');
            return;
        }
        setCreando(true);
        setMsgProducto('');
        try {
            const res = await fetch(baseUrl + "my/products", {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ name: nuevo.name.trim(), uomId: nuevo.uomId * 1 || 0 }),
            }).then(r => r.json());
            if (!res?.success) throw new Error(res?.error || 'No se pudo crear el producto');
            setMsgProducto(res.data?.existed ? 'Ya tenías un producto con ese nombre' : 'Producto creado ✓');
            setNuevo({ name: '', uomId: '' });
            cargarProductos();
        } catch (e) {
            setMsgProducto(e.message || 'No se pudo crear el producto');
        } finally {
            setCreando(false);
        }
    };

    const cargar = async () => {
        setCargando(true);
        setError('');
        try {
            const res = await fetch(baseUrl + "my/stock", {
                headers: { 'Authorization': `Bearer ${token}` }
            }).then(r => r.json());

            if (!res?.success) throw new Error(res?.error || 'Error al obtener el stock');
            setTotal(res.totalPallets || 0);
            setData(res.data || []);
            setLayouts({});
            setMapaAbierto(null);
            await cargarProductos();
        } catch (e) {
            console.error(e);
            setError(e.message || 'No se pudo cargar el stock');
        } finally {
            setCargando(false);
        }
    };

    if (cargando) {
        return (
            <div className="stock">
                <div className="stockCargando"><img src={Cargando} alt="Cargando" /></div>
            </div>
        );
    }

    return (
        <div className="stock">
            <div className="stockHeader">
                <div className="stockHeaderText">
                    <h2>Mi stock</h2>
                    <p>Inventario consignado en tus almacenes</p>
                </div>
                <button type="button" className="stockBtnRefresh" onClick={cargar}>
                    <IconRefresh />
                    Actualizar
                </button>
            </div>

            {error && <div className="stockError">{error}</div>}

            {!error && (
                <>
                    <div className="stockResumen">
                        <div className="stockResumenItem principal">
                            <span className="stockResumenNum">{total}</span>
                            <span className="stockResumenLbl">unidades en total</span>
                        </div>
                        <div className="stockResumenItem">
                            <span className="stockResumenNum">{disponibles}</span>
                            <span className="stockResumenLbl">disponibles</span>
                        </div>
                        <div className="stockResumenItem">
                            <span className="stockResumenNum">{listaGrupos.length}</span>
                            <span className="stockResumenLbl">{listaGrupos.length === 1 ? 'almacén' : 'almacenes'}</span>
                        </div>
                        <div className="stockResumenItem">
                            <span className="stockResumenNum">{productosCatalogo}</span>
                            <span className="stockResumenLbl">
                                {productosCatalogo === 1 ? 'producto en catálogo' : 'productos en catálogo'}
                            </span>
                        </div>
                    </div>

                    {data.length === 0 ? (
                        <div className="stockVacio">
                            <IconWarehouse />
                            <strong>Aún no tienes inventario</strong>
                            <span>Cuando ingreses mercancía a un almacén, aparecerá aquí.</span>
                        </div>
                    ) : (
                        <div className="stockLista">
                            {listaGrupos.map(grupo => {
                                const abierto = mapaAbierto === grupo.idStore;
                                const layout = layouts[grupo.idStore];
                                const qtyGrupo = grupo.rows.reduce((s, q) => s + (q.quantity || 0), 0);
                                const posMias = grupo.rows.filter(q => labelPosicion(q.warehouse)).length;

                                return (
                                    <section className={"stockGrupo" + (abierto ? ' abierto' : '')} key={grupo.idStore}>
                                        <div className="stockGrupoTop">
                                            <div className="stockGrupoIdent">
                                                <span className="stockGrupoIcon"><IconWarehouse /></span>
                                                <div>
                                                    <h3 className="stockGrupoNombre">{grupo.storeName}</h3>
                                                    <p className="stockGrupoMeta">
                                                        {qtyGrupo} uds · {grupo.rows.length} {grupo.rows.length === 1 ? 'línea' : 'líneas'}
                                                        {posMias > 0 ? ` · ${posMias} ${posMias === 1 ? 'posición' : 'posiciones'}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className={"stockMapToggle" + (abierto ? ' activo' : '')}
                                                onClick={() => grupo.idStore && toggleMapa(grupo.idStore)}
                                                disabled={!grupo.idStore}
                                            >
                                                <IconMap />
                                                {cargandoMapa === grupo.idStore
                                                    ? 'Cargando…'
                                                    : (abierto ? 'Ocultar mapa' : 'Ver mapa')}
                                            </button>
                                        </div>

                                        <div className="stockLineas">
                                            {grupo.rows.map((q, i) => {
                                                const posicion = labelPosicion(q.warehouse);
                                                return (
                                                    <article className="stockLinea" key={i}>
                                                        <div className="stockLineaMain">
                                                            <span className="stockLineaIcon"><IconBox /></span>
                                                            <div>
                                                                <strong className="stockLineaProd">{q.product}</strong>
                                                                <span className={"stockLineaPos" + (posicion ? '' : ' sin')}>
                                                                    {posicion ? `Posición ${posicion}` : 'Sin posición asignada'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="stockLineaNums">
                                                            <div>
                                                                <em>Total</em>
                                                                <b>{q.quantity}</b>
                                                            </div>
                                                            <div>
                                                                <em>Reservado</em>
                                                                <b>{q.reserved}</b>
                                                            </div>
                                                            <div className="disp">
                                                                <em>Disponible</em>
                                                                <b>{q.available}</b>
                                                            </div>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>

                                        {abierto && (
                                            <div className="stockLayout">
                                                {cargandoMapa === grupo.idStore && !layout && (
                                                    <div className="stockLayoutLoading">Cargando plano del almacén…</div>
                                                )}
                                                {layout && (
                                                    <>
                                                        <div className="stockLayoutHead">
                                                            <div>
                                                                <h4>Mapa de posiciones</h4>
                                                                <p>Toca una casilla naranja para ver el detalle.</p>
                                                            </div>
                                                            {(layout.grid || (layout.levels && layout.levels.length > 0)) && (
                                                                <div className="stockRackLeyenda">
                                                                    <span><i className="mia" /> Tuyo</span>
                                                                    <span><i className="ocupada" /> Ocupada</span>
                                                                    <span><i className="vacia" /> Libre</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {layout.grid && (
                                                            <div className="stockRackWrap">
                                                                <div
                                                                    className="stockRack"
                                                                    style={{
                                                                        '--rack-cols': layout.grid.cols,
                                                                        gridTemplateColumns: `1.75rem repeat(${layout.grid.cols}, minmax(2.1rem, 2.6rem))`,
                                                                    }}
                                                                >
                                                                    <div className="stockRackCorner" aria-hidden="true" />
                                                                    {Array.from({ length: layout.grid.cols }, (_, c) => (
                                                                        <div className="stockRackHead col" key={'c' + c}>{c + 1}</div>
                                                                    ))}
                                                                    {Array.from({ length: layout.grid.rows }, (_, r) => (
                                                                        <React.Fragment key={'r' + r}>
                                                                            <div className="stockRackHead fila">{r + 1}</div>
                                                                            {Array.from({ length: layout.grid.cols }, (_, c) => {
                                                                                const celda = layout.grid.cells.find(cc => cc.row === r + 1 && cc.col === c + 1);
                                                                                if (!celda) return <div className="stockCelda hueco" key={c} />;
                                                                                const clase = celda.mine ? 'mia' : celda.occupied ? 'ocupada' : 'vacia';
                                                                                const qty = celda.mine
                                                                                    ? (celda.products || []).reduce((s, p) => s + (p.quantity || 0), 0)
                                                                                    : 0;
                                                                                const label = labelCelda(celda.name, celda.row, celda.col);
                                                                                return (
                                                                                    <button
                                                                                        type="button"
                                                                                        key={c}
                                                                                        className={'stockCelda ' + clase}
                                                                                        title={celda.mine ? `${label} · ${qty} uds` : label}
                                                                                        onClick={() => celda.mine && setCeldaSel(celda)}
                                                                                        aria-label={label}
                                                                                    >
                                                                                        {celda.mine ? <span className="stockCeldaQty">{qty}</span> : null}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {layout.levels?.map(nivel => (
                                                            <div className="stockNivel" key={nivel.name}>
                                                                <h4>{nivel.name}</h4>
                                                                <div className="stockNivelGrid">
                                                                    {nivel.positions.map(p => (
                                                                        <button
                                                                            type="button"
                                                                            key={p.id}
                                                                            className={'stockCelda ' + (p.mine ? 'mia' : p.occupied ? 'ocupada' : 'vacia')}
                                                                            title={p.name}
                                                                            onClick={() => p.mine && setCeldaSel(p)}
                                                                        >
                                                                            {p.name}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {!layout.grid && !(layout.levels && layout.levels.length) && (
                                                            <div className="stockLayoutEmpty">
                                                                Este almacén aún no tiene un plano de posiciones configurado.
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                        </div>
                    )}

                    {celdaSel && (
                        <div className="stockCeldaModal" onClick={() => setCeldaSel(null)}>
                            <div className="stockCeldaModalBox" onClick={e => e.stopPropagation()}>
                                <button type="button" className="cerrar" onClick={() => setCeldaSel(null)} aria-label="Cerrar">✕</button>
                                <span className="stockModalBadge">Tu posición</span>
                                <h4>{labelCelda(celdaSel.name, celdaSel.row, celdaSel.col)}</h4>
                                {celdaSel.pallet ? <p className="pallet">Pallet {celdaSel.pallet}</p> : null}
                                <ul>
                                    {(celdaSel.products || []).map((p, i) => (
                                        <li key={i}>
                                            <span>{p.product}</span>
                                            <b>{p.quantity}</b>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    <section className="stockProductos">
                        <div className="stockProductosHead">
                            <h3>Mis productos</h3>
                            <p>
                                Catálogo propio para indicar qué entra y sale en tus In/Out.
                                Se registra en el inventario del almacén a tu nombre.
                            </p>
                        </div>
                        {productos.length > 0 && (
                            <ul className="listaProductos">
                                {productos.map(p => (
                                    <li key={p.id}>
                                        <span className="prodIcon"><IconBox /></span>
                                        <div>
                                            <b>{p.name}</b>
                                            <span>{p.uom || 'Sin unidad'}</span>
                                        </div>
                                        <i>{p.code}</i>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="nuevoProducto">
                            <input
                                type="text"
                                placeholder="Nombre del producto (ej. Caja zapatos)"
                                value={nuevo.name}
                                onChange={(e) => setNuevo({ ...nuevo, name: e.target.value })}
                            />
                            <select value={nuevo.uomId} onChange={(e) => setNuevo({ ...nuevo, uomId: e.target.value })}>
                                <option value="">Unidad de medida</option>
                                {uoms.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                            <button type="button" className="stockBtnPrimary" disabled={creando} onClick={crearProducto}>
                                {creando ? 'Creando…' : 'Crear producto'}
                            </button>
                        </div>
                        {msgProducto && <div className="stockMsg">{msgProducto}</div>}
                    </section>
                </>
            )}
        </div>
    );
};

export default Stock;
